import type { SegmentProposal, SegmentResult, SparkProposalInvitation } from '@/core/data/spark/spark.interface';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';

export interface SparkResultEntry {
  segment: SegmentResult;
  proposal?: SegmentProposal;
}

const DEFAULT_TIME_ZONE = 'UTC';

const flattenSparkResults = (segments: SegmentResult[]): SparkResultEntry[] => {
  const results: SparkResultEntry[] = [];

  segments.forEach((segment) => {
    if (segment.proposals.length) {
      segment.proposals.forEach((proposal) => {
        results.push({ segment, proposal });
      });
      return;
    }

    results.push({ segment });
  });

  return results;
};

const getInvitationForUser = (
  proposal: SegmentProposal,
  currentUserId?: string | null,
): SparkProposalInvitation | null => {
  if (!currentUserId || !Array.isArray(proposal.invitations)) {
    return null;
  }

  return proposal.invitations.find((invitation) => invitation.userId === currentUserId) || null;
};

export const hasUnansweredSparkInvitation = (proposal: SegmentProposal, currentUserId?: string | null): boolean => {
  const invitation = getInvitationForUser(proposal, currentUserId);

  if (!invitation || invitation.withdrawn === true) {
    return false;
  }

  return invitation.accepted === null || invitation.accepted === undefined;
};

export const getSparkProposalAttendeeCount = (proposal?: SegmentProposal | null): number => {
  return proposal?.confirmedUsers?.length ?? 0;
};

export const getSparkGambitAttendeeCount = (
  gambit: Pick<GambitSearchResultDto, 'attendeeCount' | 'attendees'>,
): number => {
  if (typeof gambit.attendeeCount === 'number') {
    return gambit.attendeeCount;
  }

  return gambit.attendees?.length ?? 0;
};

const compareSparkIdentity = (params: {
  leftStart: string;
  rightStart: string;
  leftLocationId: string;
  rightLocationId: string;
  leftId: string;
  rightId: string;
}): number => {
  const startDiff = new Date(params.leftStart).getTime() - new Date(params.rightStart).getTime();

  if (startDiff !== 0) {
    return startDiff;
  }

  const locationDiff = params.leftLocationId.localeCompare(params.rightLocationId);

  if (locationDiff !== 0) {
    return locationDiff;
  }

  return params.leftId.localeCompare(params.rightId);
};

export const sortSparkGambitsByAttendees = <T extends GambitSearchResultDto>(gambits: T[]): T[] => {
  const seenIds = new Set<string>();

  return [...gambits]
    .filter((gambit) => {
      if (seenIds.has(gambit.id)) {
        return false;
      }

      seenIds.add(gambit.id);
      return true;
    })
    .sort((left, right) => {
      const attendeeDiff = getSparkGambitAttendeeCount(right) - getSparkGambitAttendeeCount(left);

      if (attendeeDiff !== 0) {
        return attendeeDiff;
      }

      return compareSparkIdentity({
        leftStart: String(left.start || ''),
        rightStart: String(right.start || ''),
        leftLocationId: left.locationId || '',
        rightLocationId: right.locationId || '',
        leftId: left.id,
        rightId: right.id,
      });
    });
};

const compareSharedSparkResultOrder = (left: SparkResultEntry, right: SparkResultEntry): number => {
  const leftAttendees = left.proposal ? getSparkProposalAttendeeCount(left.proposal) : left.segment.matchCount;
  const rightAttendees = right.proposal ? getSparkProposalAttendeeCount(right.proposal) : right.segment.matchCount;

  if (leftAttendees !== rightAttendees) {
    return rightAttendees - leftAttendees;
  }

  return compareSparkIdentity({
    leftStart: left.proposal?.start || left.segment.start,
    rightStart: right.proposal?.start || right.segment.start,
    leftLocationId: left.proposal?.location?.id || left.segment.location.id,
    rightLocationId: right.proposal?.location?.id || right.segment.location.id,
    leftId: left.proposal?.id || '',
    rightId: right.proposal?.id || '',
  });
};

const buildSyntheticSegment = (proposal: SegmentProposal, fallbackTimeZone?: string | null): SegmentResult | null => {
  if (!proposal.location) {
    return null;
  }

  const matchedUsers = proposal.matchedUsers || [];

  return {
    start: proposal.start,
    end: proposal.end,
    matchedUsers,
    matchCount: matchedUsers.length,
    location: proposal.location,
    timeZone: proposal.timeZone || fallbackTimeZone || DEFAULT_TIME_ZONE,
    proposals: [proposal],
  };
};

export const mergeSparkResultsWithInvitedProposals = (params: {
  intentResults: SegmentResult[];
  invitedProposals: SegmentProposal[];
  invitedChallenges: GambitSearchResultDto[];
  invitedGambits: GambitSearchResultDto[];
  currentUserId?: string | null;
  fallbackTimeZone?: string | null;
  priorityProposalId?: string | null;
  currentIntentId?: string | null;
  priorityProposalIntentId?: string | null;
  priorityProposal?: SegmentProposal | null;
}): SparkResultEntry[] => {
  const {
    intentResults,
    invitedProposals,
    currentUserId,
    fallbackTimeZone,
    priorityProposalId,
    currentIntentId,
    priorityProposalIntentId,
    priorityProposal,
  } = params;
  const isPriorityIntentScopedMatch =
    !priorityProposalId || !priorityProposalIntentId || priorityProposalIntentId === currentIntentId;
  const effectivePriorityProposalId = isPriorityIntentScopedMatch ? priorityProposalId : null;
  const effectivePriorityProposal = isPriorityIntentScopedMatch ? priorityProposal : null;

  const baseResults = flattenSparkResults(intentResults);
  const prioritizedInvitedProposals = [...invitedProposals]
    .filter((proposal) => hasUnansweredSparkInvitation(proposal, currentUserId))
    .sort((left, right) => {
      const attendeeDiff = getSparkProposalAttendeeCount(right) - getSparkProposalAttendeeCount(left);

      if (attendeeDiff !== 0) {
        return attendeeDiff;
      }

      return compareSparkIdentity({
        leftStart: left.start,
        rightStart: right.start,
        leftLocationId: left.location?.id || '',
        rightLocationId: right.location?.id || '',
        leftId: left.id,
        rightId: right.id,
      });
    });

  const resultByProposalId = new Map<string, SparkResultEntry>();

  baseResults.forEach((entry) => {
    if (!entry.proposal || resultByProposalId.has(entry.proposal.id)) {
      return;
    }

    resultByProposalId.set(entry.proposal.id, entry);
  });

  const prioritizedResults: SparkResultEntry[] = [];
  const prioritizedProposalIds = new Set<string>();

  prioritizedInvitedProposals.forEach((proposal) => {
    if (prioritizedProposalIds.has(proposal.id)) {
      return;
    }

    const existingResult = resultByProposalId.get(proposal.id);
    if (existingResult) {
      prioritizedResults.push(existingResult);
      prioritizedProposalIds.add(proposal.id);
      return;
    }

    const syntheticSegment = buildSyntheticSegment(proposal, fallbackTimeZone);

    if (!syntheticSegment) {
      return;
    }

    prioritizedResults.push({ segment: syntheticSegment, proposal });
    prioritizedProposalIds.add(proposal.id);
  });

  baseResults.forEach((entry) => {
    const proposalId = entry.proposal?.id;

    if (proposalId && prioritizedProposalIds.has(proposalId)) {
      return;
    }

    prioritizedResults.push(entry);
  });

  const hydratedPriorityProposal =
    effectivePriorityProposalId &&
    effectivePriorityProposal?.id === effectivePriorityProposalId &&
    !prioritizedProposalIds.has(effectivePriorityProposalId)
      ? effectivePriorityProposal
      : null;

  if (hydratedPriorityProposal) {
    const syntheticSegment = buildSyntheticSegment(hydratedPriorityProposal, fallbackTimeZone);

    if (syntheticSegment) {
      prioritizedResults.unshift({
        segment: syntheticSegment,
        proposal: hydratedPriorityProposal,
      });
      prioritizedProposalIds.add(hydratedPriorityProposal.id);
    }
  }

  const dedupedPrioritizedResults: SparkResultEntry[] = [];
  const seenProposalIds = new Set<string>();

  prioritizedResults.forEach((entry) => {
    const proposalId = entry.proposal?.id;

    if (proposalId && seenProposalIds.has(proposalId)) {
      return;
    }

    if (proposalId) {
      seenProposalIds.add(proposalId);
    }

    dedupedPrioritizedResults.push(entry);
  });

  const invitationEntries = dedupedPrioritizedResults
    .filter((entry) => !!entry.proposal && prioritizedProposalIds.has(entry.proposal.id))
    .sort(compareSharedSparkResultOrder);
  const remainingProposalEntries = dedupedPrioritizedResults
    .filter((entry) => !!entry.proposal && !prioritizedProposalIds.has(entry.proposal.id))
    .sort(compareSharedSparkResultOrder);
  const matchOnlyEntries = dedupedPrioritizedResults
    .filter((entry) => !entry.proposal)
    .sort(compareSharedSparkResultOrder);

  const sortedResults = [...invitationEntries, ...remainingProposalEntries, ...matchOnlyEntries];

  if (!effectivePriorityProposalId) {
    return sortedResults;
  }

  const priorityIndex = sortedResults.findIndex((entry) => entry.proposal?.id === effectivePriorityProposalId);

  if (priorityIndex <= 0) {
    return sortedResults;
  }

  const prioritizedEntry = sortedResults[priorityIndex];
  const nextResults = [...sortedResults];
  nextResults.splice(priorityIndex, 1);
  nextResults.unshift(prioritizedEntry);

  return nextResults;
};
